import { AddressZero } from '@ethersproject/constants'
import { ChevronDownIcon, DocumentTextIcon, LockClosedIcon } from '@heroicons/react/outline'
import { QuestionMarkCircleIcon as SolidQuestionMarkCircleIcon } from '@heroicons/react/solid'
import { GlobeIcon } from "@heroicons/react/outline";
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Chip from 'app/components/Chip'
import { CurrencyLogo } from 'app/components/CurrencyLogo'
import { DiscordIcon, InstagramIcon, LockIcon, MediumIcon, RestrictedIcon, TwitterIcon } from 'app/components/Icon'
import QuestionHelper from 'app/components/QuestionHelper'
import Typography from 'app/components/Typography'
import AuctionCardPrice from 'app/features/miso/AuctionCard/AuctionCardPrice'
import logo from '../../../../public/images/logo-light.svg'
import fakeLogo from '../../../../public/images/fakeLogo.png'
/* import AuctionIcon from 'app/features/miso/AuctionIcon'
import AuctionTimer from 'app/features/miso/AuctionTimer' */
import { AuctionStatus } from 'app/features/miso/context/types'
import {
  AuctionHelperTextByTemplateId,
  AuctionPriceHelperTextByTemplateId,
  AuctionStatusById,
  AuctionTitleByTemplateId,
} from 'app/features/miso/context/utils'
import { classNames, formatNumber } from 'app/functions'
import { cloudinaryLoader } from 'app/functions/cloudinary'
import Image from 'next/image'
import Link from 'next/link'
import React, { FC, useState } from 'react'
import { LAUNCHPAD_EXTRA } from 'app/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'

import { Auction } from '../context/Auction'
import AuctionSkeleton from './AuctionSkeleton'

const chainLogos = {
  [43114]: { img: '/img/avx_ic.svg', label: 'Avax' },
  [40]: { img: '/img/telos.svg', label: 'Telos' },
  [137]: { img: '/img/matic.svg', label: 'Polygon' },
  [56]: { img: '/img/bsc_ic.svg', label: 'BNB' },
  [250]: { img: '/img/fantom.svg', label: 'Fantom' },
  [1]: { img: '/img/ethereum.svg', label: 'Ethereum' },
  [42161]: { img: '/img/arbitrum.svg', label: 'Arbitrum' },
  [10]: { img: '/img/optimism.svg', label: 'Optimism' },
  [41]: { img: '/img/telos.svg', label: 'Telos' },
}

const AuctionCard: FC<{ auction?: Auction; link?: boolean }> = ({ auction, link = false }) => {
  const [descriptionOpen, setDescriptionOpen] = useState(false)
  const { i18n } = useLingui()

  const extraLaunchpadInfo = LAUNCHPAD_EXTRA?.find(x => x.launchpadAddress.toLowerCase() == auction?.auctionInfo.addr.toLowerCase())

  const minPercentRaised = 100 - Number(auction?.remainingPercentageMinimum.toSignificant(2))
  const maxPercentRaised = 100 - Number(auction?.remainingPercentage.toSignificant(2))

  if (!auction) {
    return <AuctionSkeleton />
  }

  const content = (
    <div
      id="auction-card"
      className="overflow-hidden bg-cover border rounded shadow-md h-[500px]"
    >
      <div className="bg-[#131928] relative flex flex-col justify-between gap-3 z-[10]">
        <div className="relative h-[120px] bg-gradient-to-br from-[#3551dd] to-[#02dab2] rounded-t-[4px] ">

          {/* {extraLaunchpadInfo?.kyc ? (
            <div className='left-0 z-40 mt-2 ml-2 w-[10px]'>
              <QuestionHelper
                // @ts-ignore TYPE NEEDS FIXING
                text={"KYC Passed"}
                icon={<FontAwesomeIcon icon={faCheckCircle} style={{ color: 'rgb(98, 176, 30)' }} size={'1x'} />}
              />
            </div>
          ) : <></>} */}

          <div className="absolute w-[150px] top-[60%] left-[50%] translate-x-[-50%] translate-y-[-60%]">
            <picture>
              <img src={auction?.auctionDocuments.icon ? auction?.auctionDocuments?.icon : logo.src} alt="token logo" width={150} height={30} />
            </picture>
          </div>
          <div className="absolute flex gap-2 top-2 left-2">
            {auction?.pointListAddress !== AddressZero && (
              <div className="flex gap-1.5 items-center justify-center">
                <LockClosedIcon width={14} />
                <Typography variant="sm" weight={700} className="text-secondary">
                  {i18n._(t`Private`)}
                </Typography>
              </div>
            )}
            {auction?.auctionDocuments.bannedCountries && (
              <div className="flex gap-1.5 items-center justify-center pt-1.5">
                <LockIcon width={14} className=" fill-yellow" />
                <Typography variant="xs" weight={500} className="text-white">
                  {i18n._(t`Restricted`)}
                </Typography>
              </div>
            )}
          </div>
          {auction?.auctionDocuments.category ? (
            <Chip label={auction?.auctionDocuments.category} className="absolute bg-[#ffffff31] backdrop-blur-3xl top-2 right-2 font-medium text-sm" />
          ) : (
            <Chip label={i18n._(t`Uncategorized`)} className="absolute bg-[#ffffff31] backdrop-blur-3xl top-2 right-2 font-medium text-sm" />
          )}
        </div>

        <div className="flex-col gap-3 px-3">
          <div className="flex justify-between overflow-hidden">
            <Typography variant="h3" weight={700} className="truncate text-high-emphesis">
              {extraLaunchpadInfo?.nameOverride != null && extraLaunchpadInfo?.nameOverride != '' ? extraLaunchpadInfo?.nameOverride : auction?.auctionToken.name}
            </Typography>
            <div className='flex gap-1.5 items-center'>
              <Image src={chainLogos[auction?.paymentToken.chainId].img} alt="network icon" width={24} height={24} />
              <Typography variant="sm" weight={700} className="truncate text-high-emphesis">
                {chainLogos[auction?.paymentToken.chainId].label}
              </Typography>
            </div>
          </div>
          <div className="flex justify-between mt-3">
            <div className="flex gap-2">
              {auction?.auctionDocuments?.twitter && (<a target="_blank" rel="noreferrer" href={auction?.auctionDocuments?.twitter}><TwitterIcon width={16} height={16} className="text-white transition-colors ease-in-out delay-75 cursor-pointer motion-safe:hover:text-pink" /></a>)}
              {auction?.auctionDocuments?.discord && (<a target="_blank" rel="noreferrer" href={auction?.auctionDocuments?.discord}><DiscordIcon width={16} height={16} className="text-white transition-colors ease-in-out delay-75 cursor-pointer motion-safe:hover:text-pink" /></a>)}
              {auction?.auctionDocuments?.medium && (<a target="_blank" rel="noreferrer" href={auction?.auctionDocuments?.medium}><MediumIcon width={16} height={16} className="text-white transition-colors ease-in-out delay-75 cursor-pointer motion-safe:hover:text-pink" /></a>)}
              {auction?.auctionDocuments?.website && (<a target="_blank" rel="noreferrer" href={auction?.auctionDocuments?.website}><GlobeIcon width={16} height={16} className="text-white transition-colors ease-in-out delay-75 cursor-pointer motion-safe:hover:text-pink" /></a>)}
              {auction?.auctionDocuments?.whitepaper && (<a target="_blank" rel="noreferrer" href={auction?.auctionDocuments?.whitepaper}><DocumentTextIcon width={16} height={16} className="text-white transition-colors ease-in-out delay-75 cursor-pointer motion-safe:hover:text-pink" /></a>)}
            </div>
            <button onClick={() => setDescriptionOpen(!descriptionOpen)} className="flex gap-0.5 items-center group">
              <Typography variant="sm" weight={700} className="transition-colors ease-in-out delay-75 text-high-emphesis motion-safe:group-hover:text-pink">
                {i18n._(t`Description`)}
              </Typography>
              <ChevronDownIcon width={16} height={16} className={`text-white transition-all ease-in-out delay-75 motion-safe:group-hover:text-pink ${descriptionOpen ? 'transform scale-[-1]' : 'transform scale-[1]'}`} />
            </button>
          </div>
        </div>
        <div className={`flex-col space-y-3 transition-all ease-in-out delay-75 ${!descriptionOpen ? 'h-0 invisible overflow-hidden' : ' h-[152px] visible overflow-y-auto '}`}>
          <div className='h-[100%] px-3 py-2'>
            <Typography variant="sm" weight={700} className="text-high-emphesis">
              {auction.auctionDocuments.description ? auction.auctionDocuments.description : 'No description provided'}
            </Typography>
          </div>
        </div>
        <div className={`flex-col space-y-3 transition-all ease-in-out delay-75 ${descriptionOpen ? 'h-0 invisible overflow-hidden' : ' h-[202px] visible overflow-hidden'}`}>
          <div className={classNames('flex items-center justify-between p-3')}>
            <div className="flex items-center">
              <div className="flex gap-3">
                <Typography variant="xs" weight={700}>
                  {/*@ts-ignore TYPE NEEDS FIXING*/}
                  {AuctionTitleByTemplateId(i18n)[auction?.template]}
                </Typography>
              </div>
              <QuestionHelper
                // @ts-ignore TYPE NEEDS FIXING
                text={AuctionHelperTextByTemplateId(i18n)[auction?.template]}
                icon={<SolidQuestionMarkCircleIcon width={12} height={12} />}
              />
            </div>
            <Typography
              variant="xs"
              weight={700}
              className={
                auction?.status === AuctionStatus.LIVE
                  ? 'text-green'
                  : auction?.status === AuctionStatus.FINISHED
                    ? 'text-pink'
                    : auction?.status === AuctionStatus.UPCOMING
                      ? 'text-blue'
                      : ''
              }
            >
              {AuctionStatusById(i18n)[auction?.status]}
            </Typography>
          </div>
          <div className="flex justify-between px-3">

            {/* <div className="flex flex-col gap-0.5">
              <div className="flex items-center">
                <Typography variant="xxs" weight={700} className="uppercase text-secondary">
                  {i18n._(t`Token Price`)}
                </Typography>
                <QuestionHelper
                  // @ts-ignore TYPE NEEDS FIXING
                  text={AuctionPriceHelperTextByTemplateId(i18n)[auction?.template]}
                  icon={<SolidQuestionMarkCircleIcon width={10} height={10} className="text-secondary mb-[2px]" />}
                />
              </div>
              <Typography variant="xs" weight={700}>
                {formatNumber(auction?.tokenPrice?.toSignificant(6))} {auction?.tokenPrice?.quoteCurrency.symbol}
              </Typography>
            </div> */}

            <div className="flex flex-col gap-0.5">
              <div className="flex items-center">
                <Typography variant="xxs" weight={700} className="uppercase text-secondary">
                  {i18n._(t`Launch Price`)}
                </Typography>
              </div>
              <div className="relative flex items-center">
                <Typography variant="xs" weight={700}>
                  {(extraLaunchpadInfo?.launchPrice ?? auction.tokenPrice.toFixed(4)) + " " + (extraLaunchpadInfo?.launchPriceToken ?? auction.tokenPrice?.quoteCurrency.symbol)}
                </Typography>
              </div>
            </div>

            <div className="flex flex-col gap-0.5 items-end">
              <div className="flex items-center">
                <Typography variant="xxs" weight={700} className="uppercase text-secondary">
                  {i18n._(t`Token Price`)}
                </Typography>
                <QuestionHelper
                  // @ts-ignore TYPE NEEDS FIXING
                  text={AuctionHelperTextByTemplateId(i18n)[auction?.template]}
                  icon={<SolidQuestionMarkCircleIcon width={10} height={10} className="text-secondary mb-[2px]" />}
                />
              </div>
              <div className="relative flex items-center">
                <div className="!w-4 !h-4 absolute -left-7 -top-2">
                  <CurrencyLogo
                    currency={auction?.paymentToken}
                    size={32}
                    className="!rounded-full overflow-hidden transform scale-[0.5]"
                  />
                </div>
                <Typography variant="xs" weight={700}>
                  <AuctionCardPrice auction={auction} />
                </Typography>
              </div>
            </div>
          </div>
          <div className='flex-col gap-1 px-3 pt-2 pb-1'>
            <div className="flex justify-between">
              <span className="text-base font-medium">Minimum Raised</span>

              <span className="text-sm font-medium dark:text-white">{`${minPercentRaised && minPercentRaised > 100 ? 100 : minPercentRaised}%`}</span>
            </div>
            <div className="w-full bg-[#323749] rounded-full h-2.5 mb-2">
              <div className="token-progress h-2.5 rounded-full" style={{ width: `${minPercentRaised && minPercentRaised > 100 ? 100 : minPercentRaised}%` }}></div>
            </div>
          </div>
          <div className='flex-col gap-1 px-3'>
            <div className="flex justify-between">
              <span className="text-base font-medium">Total Raised</span>

              <span className="text-sm font-medium dark:text-white">{maxPercentRaised}%</span>
            </div>
            <div className="w-full bg-[#323749] rounded-full h-2.5 mb-2">
              <div className="token-progress h-2.5 rounded-full" style={{ width: `${maxPercentRaised}%` }}></div>
            </div>
          </div>
        </div>
        <div className="px-3 py-2">
          <Link href={`/launchpad/${auction?.auctionInfo.addr}`} passHref={true}>
            <button className="gradient-button">{i18n._(t`View Details`)}</button>
          </Link>
        </div>
      </div>
    </div >
  )

  if (!link) return content

  return (
    <Link href={`/launchpad/${auction.auctionInfo.addr}`} passHref={true}>
      <a>{content}</a>
    </Link>
  )
}

export default AuctionCard
